import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { Language, TranslationKeys } from "../types/translation";
import { enTranslations } from "../translations/en";
import { esTranslations } from "../translations/es";
import { parityEn, parityEs } from "../translations/parity";

interface LanguageContextType {
  currentLanguage: Language;
  setLanguage: (language: Language) => void;
  translations: TranslationKeys;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// The app-parity screens keep their strings in translations/parity/, one file per feature.
const translations: Record<Language, TranslationKeys> = {
  en: { ...enTranslations, ...parityEn } as TranslationKeys,
  es: { ...esTranslations, ...parityEs } as TranslationKeys,
};

function translateKey(
  currentLanguage: Language,
  key: string,
  params?: Record<string, string | number>,
): string {
  try {
    const keys = key.split(".");
    let value: any = translations[currentLanguage];

    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === "object" && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            console.warn(`Translation key not found: ${key}`);
            return key;
          }
        }
        break;
      }
    }

    if (typeof value !== "string") {
      console.warn(`Translation key "${key}" does not resolve to a string`);
      return key;
    }

    if (params) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
        return paramKey in params ? String(params[paramKey]) : match;
      });
    }

    return value;
  } catch (error) {
    console.error(`Error translating key "${key}":`, error);
    return key;
  }
}

const fallbackLanguageContext: LanguageContextType = {
  currentLanguage: "en",
  setLanguage: () => {},
  translations: enTranslations,
  t: (key, params) => translateKey("en", key, params),
};

interface LanguageProviderProps {
  children: ReactNode;
}

function detectInitialLanguage(): Language {
  if (typeof window === "undefined") return "en";
  // 1. User-saved preference always wins
  const saved = localStorage.getItem("language");
  if (saved === "en" || saved === "es") return saved;
  // 2. Auto-detect from browser
  const browserLang = (navigator.language || "").toLowerCase();
  return browserLang.startsWith("es") ? "es" : "en";
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(
    detectInitialLanguage,
  );

  // Keep <html lang="..."> in sync for accessibility & SEO
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = currentLanguage;
    }
  }, [currentLanguage]);

  // Save language preference to localStorage when it changes
  const setLanguage = (language: Language) => {
    setCurrentLanguage(language);
    localStorage.setItem("language", language);
  };

  const t = (key: string, params?: Record<string, string | number>): string =>
    translateKey(currentLanguage, key, params);

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    translations: translations[currentLanguage],
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    if (import.meta.env.DEV) {
      console.warn("useTranslation used outside LanguageProvider; falling back to English");
    }
    return fallbackLanguageContext;
  }
  return context;
}
